// ============================================================================
// DATA VALIDATION API CLIENT
// ============================================================================
// API client for data validation endpoints
// ============================================================================

import { ApiClient } from '../../core/apiClient';

// ============================================================================
// TYPES
// ============================================================================

export interface ValidationResult {
  rule_id: string;
  record_id: number;
  field_name: string;
  status: 'pass' | 'warning' | 'fail' | 'error';
  severity: 'low' | 'medium' | 'high' | 'critical';
  message: string;
  suggested_action?: string;
  timestamp: string;
  rule_name?: string;
}

export interface ValidationRule {
  id: string;
  name: string;
  description: string;
  type: ValidationType;
  field_types: string[];
  parameters: Record<string, any>;
  is_active: boolean;
  severity: ValidationSeverity;
  created_at: string;
  updated_at: string;
}

export interface AnomalyDetection {
  id: string;
  type: AnomalyType;
  description: string;
  severity: ValidationSeverity;
  confidence: number;
  detected_at: string;
  resolved_at?: string;
  resolution_notes?: string;
  affected_records: string[];
  metrics?: {
    expected_value?: number;
    actual_value?: number;
    deviation_percentage?: number;
    z_score?: number;
  };
}

export interface DataQualityReport {
  record_id: number;
  overall_score: number;
  completeness_score: number;
  accuracy_score: number;
  consistency_score: number;
  validity_score: number;
  timeliness_score: number;
  issues: string[];
  recommendations: string[];
  generated_at: string;
}

export interface ValidationConfig {
  enable_real_time_validation: boolean;
  enable_batch_validation: boolean;
  enable_anomaly_detection: boolean;
  auto_resolve_low_severity: boolean;
  anomaly_sensitivity: number;
  notification_threshold: ValidationSeverity;
  retention_period_days: number;
}

export type ValidationType =
  | 'range_check'
  | 'required_check'
  | 'format_check'
  | 'consistency_check'
  | 'business_rule'
  | 'statistical_anomaly'
  | 'completeness_check'
  | 'duplicate_check';

export type ValidationSeverity = 'low' | 'medium' | 'high' | 'critical';

export type AnomalyType =
  | 'statistical_outlier'
  | 'trend_deviation'
  | 'pattern_anomaly'
  | 'data_quality_issue'
  | 'compliance_violation'
  | 'system_error'
  | 'fraud_detection'
  | 'seasonal_anomaly'
  | 'sudden_change'
  | 'missing_data';

// ============================================================================
// DATA VALIDATION API CLIENT CLASS
// ============================================================================

class DataValidationApiClient extends ApiClient {
  constructor() {
    super({
      baseURL: '/api/data-validation',
      enableLogging: import.meta.env.DEV
    });
  }

  // ============================================================================
  // VALIDATION RULES
  // ============================================================================

  /**
   * Get all validation rules
   */
  async getValidationRules(): Promise<ValidationRule[]> {
    const response = await this.client.get('/rules');
    return response.data.data || response.data || [];
  }

  /**
   * Get a single validation rule by ID
   */
  async getValidationRule(ruleId: string): Promise<ValidationRule | null> {
    const response = await this.client.get(`/rules/${ruleId}`);
    return response.data.data || response.data;
  }

  /**
   * Create a new validation rule
   */
  async createValidationRule(rule: ValidationRule): Promise<ValidationRule> {
    const response = await this.client.post('/rules', rule);
    return response.data.data || response.data;
  }

  /**
   * Update a validation rule
   */
  async updateValidationRule(ruleId: string, updates: Partial<ValidationRule>): Promise<boolean> {
    await this.client.put(`/rules/${ruleId}`, updates);
    return true;
  }

  /**
   * Delete a validation rule
   */
  async deleteValidationRule(ruleId: string): Promise<boolean> {
    await this.client.delete(`/rules/${ruleId}`);
    return true;
  }

  // ============================================================================
  // ANOMALIES
  // ============================================================================

  /**
   * Get all anomalies
   */
  async getAnomalies(): Promise<AnomalyDetection[]> {
    const response = await this.client.get('/anomalies');
    return response.data.data || response.data || [];
  }

  /**
   * Get a single anomaly by ID
   */
  async getAnomaly(anomalyId: string): Promise<AnomalyDetection | null> {
    const response = await this.client.get(`/anomalies/${anomalyId}`);
    return response.data.data || response.data;
  }

  /**
   * Create a new anomaly
   */
  async createAnomaly(anomaly: AnomalyDetection): Promise<AnomalyDetection> {
    const response = await this.client.post('/anomalies', anomaly);
    return response.data.data || response.data;
  }

  /**
   * Resolve an anomaly
   */
  async resolveAnomaly(anomalyId: string, resolutionNotes: string): Promise<boolean> {
    await this.client.post(`/anomalies/${anomalyId}/resolve`, { resolution_notes: resolutionNotes });
    return true;
  }

  // ============================================================================
  // CONFIGURATION
  // ============================================================================

  /**
   * Get validation configuration
   */
  async getConfig(): Promise<ValidationConfig> {
    const response = await this.client.get('/config');
    return response.data.data || response.data;
  }

  /**
   * Update validation configuration
   */
  async updateConfig(updates: Partial<ValidationConfig>): Promise<boolean> {
    await this.client.put('/config', updates);
    return true;
  }
}

// Export singleton instance
export const dataValidationApi = new DataValidationApiClient();
export default dataValidationApi;
